import isBackOffice from 'utils/appUtils';
import apiClient, { endpoints, queryStringifyObject } from 'app/apiClient';
import { PROFILE_STEP_TYPES, APPLICATION_MODE } from 'app/constants';
import navigate from 'utils/navigate';
import {
  call, put, takeLatest, all, select,
} from 'redux-saga/effects';
import dotProp from 'dot-prop-immutable';
import translate from 'app/translations';
import {
  setServiceSuccessNotification,
  setServiceErrorNotification,
} from 'notifications/notificationsActions';
import { constantToStepRoute } from './AgentProfileSteps';
import {
  SET_AGENT_PROFILE_STEP_REQUEST,
  setAgentProfileStepRequest,
  FETCH_CONSTANTS_REQUEST,
  fetchConstantsSuccess,
  serviceComplete,
  SAVE_USER_PROFILE_NEW_REQUEST,
  FETCH_CURRENT_USER_PROFILE_REQUEST,
  fetchCurrentUserProfileSuccess,
  SAVE_USER_PROFILE_IN_PROGRESS_REQUEST,
  SAVE_COMPANY_PROFILE_REQUEST,
  saveCompanyProfileSuccess,
  saveUserProfileSuccess,
  SAVE_SERVICE_FEE_REQUEST,
  saveServiceFeeSuccess,
  FETCH_REGION_COVERAGE_REQUEST,
  fetchRegionCoverageSuccess,
  FETCH_SLA_TARGETS_REQUEST,
  fetchSlaTargetsSuccess,
  fetchRegionCoverageRequest,
  fetchSlaTargetsRequest,
  SAVE_REGION_COVERAGE_REQUEST,
  saveRegionCoverageSuccess,
  SAVE_PROFESSIONAL_INFO_REQUEST,
  saveProfessionalInfoSuccess,
  SAVE_E_AND_O_INFO_REQUEST,
  saveEandOInfoSuccess,
  SAVE_CREDENTIALS_REQUEST,
  saveCredentialsSuccess,
  saveCompleteProfileSuccess,
  setCredentialDocuments,
  DELETE_CREDENTIAL_DOCUMENT_REQUEST,
  deleteCredentialDocumentSuccess,
  DELETE_E_AND_O_DOCUMENT_REQUEST,
  deleteEandODocumentSuccess,
  UPDATE_APPLICATION_STATUS_REQUEST,
  updateApplicationStatusSuccess,
  APPROVE_APPLICATION_REQUEST,
  FETCH_APPLICATION_MESSAGES_REQUEST,
  fetchApplicationMessagesSuccess,
  ADD_APPLICATION_MESSAGE_REQUEST,
  addApplicationMessageSuccess,
} from './agentProfileActions';

const t = translate;

export const apiGetRequest = endpoint => apiClient.getRequest(endpoint);
export const apiDeleteRequest = endpoint => apiClient.deleteRequest(endpoint);
export const apiPostRequest = (endpoint, payload) => apiClient.postRequest(endpoint, payload);
export const apiPatchRequest = (endpoint, payload) => apiClient.patchRequest(endpoint, payload);
export const getAgentProfile = state => dotProp.get(state, 'agentProfile') || {};

/* eslint-disable no-console */
export function* fetchConstants() {
  const constantsEndpoint = endpoints.constants;
  const sgEndpoint = endpoints.serviceGroups;

  try {
    const responseList = yield all([
      call(apiGetRequest, constantsEndpoint),
      call(apiGetRequest, sgEndpoint),
    ]);

    const constants = {
      ...responseList[0].data,
      PROGRAM_SERVICE_GROUPS: [...responseList[1].data.programServiceGroups],
    };
    yield put(fetchConstantsSuccess(constants));
  } catch (error) {
    yield put(setServiceErrorNotification(t('Generic.fetchFailedConstants')));
  }
}

export function* setAgentProfileStep(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { stepType } = action.payload;

  if (stepType && stepType === PROFILE_STEP_TYPES.COMPLETION) {
    const completionEndpoint = `applications/${applicationId}/complete${queryStringifyObject({ accessToken })}`;
    try {
      yield call(apiPostRequest, completionEndpoint, undefined);
      yield put(saveCompleteProfileSuccess());
    } catch (e) {
      yield put(setServiceErrorNotification(e.message || undefined));
    }
    navigate(`/applications/${constantToStepRoute(stepType)}/${applicationId}?accessToken=${accessToken || ''}`);
  } else if (stepType) {
    navigate(`/applications/${constantToStepRoute(stepType)}/${applicationId}?accessToken=${accessToken || ''}`);
  }

  window.scrollTo(0, 0);
}

export function* saveUserProfileRegister(action) {
  const { userProfile } = action.payload;

  const user = {
    ...userProfile,
  };

  delete user.tc;

  const payload = {
    ...userProfile.tc,
    user: {
      ...user,
    },
  };

  const endpoint = endpoints.userProfile;

  try {
    yield call(apiPostRequest, endpoint, payload);
    yield all([
      setServiceSuccessNotification(),
      serviceComplete(),
    ]);

    const nextRoute = '/next-steps';
    navigate(nextRoute);
    window.scrollTo(0, 0);
  } catch (e) {
    yield all([
      put(setServiceErrorNotification(e.message || undefined)),
      put(serviceComplete()),
    ]);
  }
}

export function* saveInProgressUserProfile(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { userProfile, nextStep } = action.payload;
  const payload = {
    user: {
      ...userProfile,
    },
  };
  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveUserProfileSuccess(userProfile)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);

    const { zipCode } = userProfile || {};
    if (zipCode) {
      yield all([
        put(fetchRegionCoverageRequest(zipCode)),
        put(fetchSlaTargetsRequest(zipCode)),
      ]);
    }
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* fetchCurrentUserProfile(action) {
  const { applicationId, accessToken } = action.payload;
  const endpoint = `${endpoints.getUserProfile}/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    const response = yield call(apiGetRequest, endpoint);

    if (!isBackOffice && response.data.status !== APPLICATION_MODE.IN_PROGRESS) {
      navigate(`/applications/${constantToStepRoute(PROFILE_STEP_TYPES.COMPLETION)}/${applicationId}?accessToken=${accessToken}`);
    } else {
      if (response.data && response.data.credentials && Array.isArray(response.data.credentials.documents)) {
        yield put(setCredentialDocuments(response.data.credentials.documents));
      }

      yield all([
        put(fetchCurrentUserProfileSuccess(response.data)),
        put(serviceComplete()),
      ]);

      const { zipCode } = response.data.user || {};
      if (zipCode) {
        yield all([
          put(fetchRegionCoverageRequest(zipCode)),
          put(fetchSlaTargetsRequest(zipCode)),
        ]);
      }
    }
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* saveCompanyProfile(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { companyProfile, nextStep } = action.payload;
  const payload = {
    company: {
      ...companyProfile,
    },
  };

  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveCompanyProfileSuccess(companyProfile)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* saveServiceFee(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { serviceFeeList } = action.payload;
  const payload = {
    fees: [...serviceFeeList],
  };

  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveServiceFeeSuccess(serviceFeeList)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* fetchRegionCoverage(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);

  const { zipCode } = action.payload;
  const endpoint = `${endpoints.coverageMaps}/${applicationId}/${zipCode}${queryStringifyObject({ accessToken })}`;

  try {
    const response = yield call(apiGetRequest, endpoint);
    yield all([
      put(fetchRegionCoverageSuccess(response.data)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* fetchSlaTargets(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);

  const { zipCode } = action.payload;
  const endpoint = `${endpoints.regionSlaTargets}/${applicationId}/${zipCode}${queryStringifyObject({ accessToken })}`;

  try {
    const response = yield call(apiGetRequest, endpoint);
    yield all([
      put(fetchSlaTargetsSuccess(response.data)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(serviceComplete()),
    ]);
  }
}

export function* saveRegionCoverage(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { nextStep, regionCoverage } = action.payload;
  const payload = {
    coverage: {
      ...regionCoverage,
    },
  };

  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveRegionCoverageSuccess(regionCoverage)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* saveProfessionalInfo(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { nextStep, professionalInfo } = action.payload;

  const payload = {
    ...professionalInfo,
  };

  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveProfessionalInfoSuccess(professionalInfo)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* saveCredentials(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { nextStep, credentials } = action.payload;
  const payload = {
    credentials: {
      ...credentials,
    },
  };

  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveCredentialsSuccess(credentials)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* deleteCredentialDocument(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { documentId } = action.payload;

  const endpoint = `applications/${applicationId}/uploads/${documentId}${queryStringifyObject({ accessToken })}`;

  try {
    yield call(apiDeleteRequest, endpoint);
    yield all([
      put(deleteCredentialDocumentSuccess(documentId)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* saveEandOInfo(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { nextStep, eandoInfo } = action.payload;
  const payload = {
    eo: {
      ...eandoInfo,
    },
  };
  const endpoint = `applications/${applicationId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(saveEandOInfoSuccess(eandoInfo)),
      put(serviceComplete()),
      put(setAgentProfileStepRequest(nextStep)),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* deleteEandODocument(action) {
  const state = yield select();
  const { applicationId, accessToken } = getAgentProfile(state);
  const { documentId } = action.payload;
  const endpoint = `applications/${applicationId}/uploads/${documentId}${queryStringifyObject({ accessToken })}`;
  try {
    yield call(apiDeleteRequest, endpoint);
    yield all([
      put(deleteEandODocumentSuccess(documentId)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}


export function* updateApplicationStatus(action) {
  const state = yield select();
  const { applicationId } = getAgentProfile(state);
  const endpoint = `applications/${applicationId}`;

  const { status } = action.payload;

  const payload = {
    status,
  };

  try {
    yield call(apiPatchRequest, endpoint, payload);
    yield all([
      put(updateApplicationStatusSuccess(status)),
      put(setServiceSuccessNotification()),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* approveApplication(action) {
  const state = yield select();
  const { applicationId } = getAgentProfile(state);
  const endpoint = `back-office/${applicationId}/approve`;

  const { status } = action.payload;

  try {
    yield call(apiPostRequest, endpoint);
    yield all([
      put(updateApplicationStatusSuccess(status)),
      put(setServiceSuccessNotification()),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification()),
      put(serviceComplete()),
    ]);
  }
}

export function* fetchApplicationMessageList() {
  const state = yield select();
  const { applicationId } = getAgentProfile(state);
  const notesEndpoint = `${endpoints.notes}/${applicationId}/notes`;

  try {
    const response = yield call(apiGetRequest, notesEndpoint);
    yield all([
      put(fetchApplicationMessagesSuccess(response)),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification(t('MessageList.requestMessageListFail'))),
      put(serviceComplete()),
    ]);
  }
}

export function* addApplicationMessage(action) {
  const state = yield select();
  const { applicationId } = getAgentProfile(state);
  const { userName, userId } = dotProp.get(state, 'app') || {};
  const notesEndpoint = `${endpoints.notes}/${applicationId}/notes`;
  const { messageText } = action.payload;
  const message = {
    notes: messageText,
    fromPersonId: userId,
    name: userName,
  };

  try {
    const response = yield call(apiPostRequest, notesEndpoint, message);
    yield all([
      put(addApplicationMessageSuccess(response)),
      put(setServiceSuccessNotification(t('PostMessage.postMessageSuccess'))),
      put(serviceComplete()),
    ]);
  } catch (error) {
    yield all([
      put(setServiceErrorNotification(t('PostMessage.postMessageError'))),
      put(serviceComplete()),
    ]);
  }
}

export default function* agentProfileSaga() {
  yield all([
    takeLatest(FETCH_CONSTANTS_REQUEST, fetchConstants),
    takeLatest(SET_AGENT_PROFILE_STEP_REQUEST, setAgentProfileStep),
    takeLatest(SAVE_USER_PROFILE_NEW_REQUEST, saveUserProfileRegister),
    takeLatest(SAVE_USER_PROFILE_IN_PROGRESS_REQUEST, saveInProgressUserProfile),
    takeLatest(FETCH_CURRENT_USER_PROFILE_REQUEST, fetchCurrentUserProfile),
    takeLatest(SAVE_COMPANY_PROFILE_REQUEST, saveCompanyProfile),
    takeLatest(SAVE_SERVICE_FEE_REQUEST, saveServiceFee),
    takeLatest(FETCH_REGION_COVERAGE_REQUEST, fetchRegionCoverage),
    takeLatest(FETCH_SLA_TARGETS_REQUEST, fetchSlaTargets),
    takeLatest(SAVE_REGION_COVERAGE_REQUEST, saveRegionCoverage),
    takeLatest(SAVE_PROFESSIONAL_INFO_REQUEST, saveProfessionalInfo),
    takeLatest(SAVE_E_AND_O_INFO_REQUEST, saveEandOInfo),
    takeLatest(DELETE_E_AND_O_DOCUMENT_REQUEST, deleteEandODocument),
    takeLatest(SAVE_CREDENTIALS_REQUEST, saveCredentials),
    takeLatest(DELETE_CREDENTIAL_DOCUMENT_REQUEST, deleteCredentialDocument),
    takeLatest(UPDATE_APPLICATION_STATUS_REQUEST, updateApplicationStatus),
    takeLatest(APPROVE_APPLICATION_REQUEST, approveApplication),
    takeLatest(FETCH_APPLICATION_MESSAGES_REQUEST, fetchApplicationMessageList),
    takeLatest(ADD_APPLICATION_MESSAGE_REQUEST, addApplicationMessage),
  ]);
}
